import * as React from 'react';
import { XIcon } from '@phosphor-icons/react';
import { cn } from '@/utils/utils';
import { IconButton } from '@/components/ui/icon-button';

interface BannerProps {
  /** What it's about; left out for a banner that is only its text and actions (cookies). */
  title?: React.ReactNode;
  children?: React.ReactNode;
  /** Buttons under the text. */
  actions?: React.ReactNode;
  /** info: news (an update rolling out); destructive: something is wrong with the user's work (a takedown). */
  tone?: 'neutral' | 'info' | 'destructive';
  /** top: centred under the top bar; bottom-start: the bottom left corner, above dialogs (consent that must be answerable while one is open). */
  place?: 'top' | 'bottom-start';
  /** Shows a dismiss button that calls this. */
  onDismiss?: () => void;
  role?: 'status' | 'alert' | 'dialog';
  'aria-label'?: string;
  className?: string;
}

/** Lureo's banner: a notice over the page that doesn't block it. */
export function Banner({ title, children, actions, tone = 'neutral', place = 'top', onDismiss, role = 'status', className, ...aria }: BannerProps) {
  return (
    <div
      role={role}
      {...aria}
      className={cn(
        'pointer-events-auto fixed flex items-start gap-3 rounded-lureo-radius-lg border-lureo-border-width bg-lureo-surface py-3 pl-5 font-lureo-font-ui shadow-lureo-shadow-modal',
        onDismiss ? 'pr-3' : 'pr-5',
        place === 'top' ? 'left-1/2 top-20 z-lureo-layer-overlay w-[min(36rem,calc(100vw-2rem))] -translate-x-1/2' : 'bottom-4 left-4 right-4 z-lureo-layer-tooltip sm:right-auto sm:max-w-md',
        tone === 'destructive' ? 'border-lureo-danger/30' : 'border-lureo-line',
        className,
      )}
    >
      {tone === 'info' && <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-lureo-sky-ink" />}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {title && <p className={cn('text-lureo-15 font-semibold leading-[1.3]', tone === 'destructive' ? 'text-lureo-danger-ink' : 'text-lureo-ink')}>{title}</p>}
        {children && <div className={cn('text-lureo-13 leading-[1.45]', title ? 'font-lureo-font-prose text-lureo-ink-subtle' : 'text-lureo-ink')}>{children}</div>}
        {actions && <div className="mt-3 flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {onDismiss && (
        <IconButton label="Dismiss" onClick={onDismiss}>
          <XIcon className="h-4 w-4" />
        </IconButton>
      )}
    </div>
  );
}
